import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ConsolidatedDto } from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import {
  formatPersonName,
  formatRatio,
  formatSharePercent,
  formatThousands,
  signTone,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/**
 * "Comparativo entre sócios" (aba interna do consolidado): participação, PL por ano, média a.a., dívidas e, com
 * política, garantia livre e sem restrição. A tela não soma: o Total por ano vem de `netWorthTotalByYear` e o das
 * participações de `sharePercentTotal`, do backend. O nome abre o sócio.
 */
@Component({
  selector: 'app-pit-comparison-table',
  standalone: true,
  templateUrl: './comparison-table.component.html',
  styleUrl: './comparison-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparisonTableComponent {
  readonly consolidated = input.required<ConsolidatedDto>();
  readonly policyDefined = input(true);
  readonly openPartner = output<string>();

  protected readonly ratio = formatRatio;
  protected readonly share = formatSharePercent;
  protected readonly thousands = formatThousands;
  protected readonly tone = signTone;

  /** Anos do comparativo (união dos anos dos sócios, crescente, até 4). */
  protected readonly years = computed(() => {
    const all = new Set<number>();
    for (const row of this.consolidated().comparison) {
      for (const item of row.netWorthByYear) all.add(item.year);
    }
    return [...all].sort((a, b) => a - b).slice(-4);
  });

  protected readonly rows = computed(() => {
    const c = this.consolidated();
    return c.comparison.map(row => ({
      row,
      displayName: formatPersonName(row.name),
      values: this.years().map(year =>
        formatThousands(row.netWorthByYear.find(item => item.year === year)?.value ?? null)
      ),
      unrestricted:
        c.partnersCoverage.find(item => item.personId === row.personId)?.unrestricted ?? null,
    }));
  });

  /** PL somado por ano, pronto do backend; ano sem total (não comparável) aparece "—". */
  private readonly totalByYear = computed(
    () =>
      new Map(
        (this.consolidated().netWorthTotalByYear ?? []).map(
          item => [item.year, item.value] as const
        )
      )
  );

  protected readonly totals = computed(() =>
    this.years().map(year => formatThousands(this.totalByYear().get(year) ?? null))
  );

  /** Algum ano da tabela ficou sem total (falta declaração de algum sócio). */
  protected readonly hasPartialYears = computed(() =>
    this.years().some(year => this.totalByYear().get(year) == null)
  );

  /** A coluna "Part." só aparece quando algum sócio tem participação conhecida. */
  protected readonly showShare = computed(() =>
    this.consolidated().comparison.some(row => row.sharePercent != null)
  );

  protected readonly caption = computed(() => {
    const collateralNote = this.policyDefined() ? ' e mesma régua de garantias' : '';
    return `R$ mil · patrimônio líquido nos ${this.years().length} anos mais recentes${collateralNote}. Clique no nome para abrir o sócio.`;
  });

  /** Soma das participações, pronta do backend (`sharePercentTotal`); sem ela, "—". */
  protected readonly shareTotal = computed(() => this.consolidated().sharePercentTotal ?? null);
}
